import { ReactElement, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import chefPlating from 'src/assets/images/chef-plating.png';
import ModalWrapper from 'src/components/ModalWrapper';
import { ICreateAttributeRuleDestination } from 'src/api/createAttributeRule';
import { getExistingRuleDestinations } from 'src/helpers/attributeRuleDestinations/attributeRuleDestinations';
import { useCreateAttributeRule } from 'src/hooks/useCreateAttributeRule';
import { useItem } from 'src/hooks/useItem/useItem';
import { IRuleDetailsValues, RuleDetailsStep } from './Steps/RuleDetailsStep';
import { IRuleDestinationsValue, RuleDestinationsStep } from './Steps/RuleDestinationsStep';
import { UNIQUE_ATTRIBUTE_RULE_NAME_ERROR } from './constants';

enum CreateAttributeRuleStep {
  Details = 0,
  Destinations = 1,
}

interface ICreateAttributeRuleModalProps {
  open: boolean;
  closeModal: () => void;
  itemId: string;
}

const CreateAttributeRuleFlow = ({ closeModal, itemId }: Omit<ICreateAttributeRuleModalProps, 'open'>) => {
  const { data: item } = useItem(itemId);
  const { mutate, isLoading, error, reset } = useCreateAttributeRule(itemId, closeModal);
  const [step, setStep] = useState(CreateAttributeRuleStep.Details);
  const [image, setImage] = useState<string | File>('');
  const [destinationsValue, setDestinationsValue] = useState<IRuleDestinationsValue>({ channelIds: [], venueIds: [] });
  const form = useForm<IRuleDetailsValues>({
    mode: 'onChange',
    defaultValues: { name: '', consumerName: '', description: '', plu: '' },
  });

  const attributeRules = item?.attribute_rules ?? [];
  const existingRuleNames = useMemo(() => attributeRules.map(({ name }) => name), [attributeRules]);
  const existingDestinations = useMemo(() => getExistingRuleDestinations(attributeRules), [attributeRules]);

  const goToDestinations = () => {
    reset();
    setStep(CreateAttributeRuleStep.Destinations);
  };

  const onSubmit = (destinations: ICreateAttributeRuleDestination[]) => {
    const { name, consumerName, description, plu } = form.getValues();
    mutate(
      {
        payload: {
          name: name.trim(),
          consumer_name: consumerName.trim() || null,
          description: description.trim() || null,
          plu: plu.trim() || null,
          destinations,
        },
        file: image instanceof File ? image : null,
      },
      {
        onError: ({ message }) => {
          if (message !== UNIQUE_ATTRIBUTE_RULE_NAME_ERROR) return;
          setStep(CreateAttributeRuleStep.Details);
          form.setError('name', { message });
        },
      },
    );
  };

  if (step === CreateAttributeRuleStep.Details) {
    return (
      <RuleDetailsStep
        form={form}
        image={image}
        setImage={setImage}
        placeholders={{
          consumer_name: item?.consumerName ?? undefined,
          description: item?.description ?? undefined,
          posId: item?.plu ?? undefined,
        }}
        existingRuleNames={existingRuleNames}
        onCancel={closeModal}
        onContinue={goToDestinations}
      />
    );
  }

  return (
    <RuleDestinationsStep
      value={destinationsValue}
      onChange={setDestinationsValue}
      existingDestinations={existingDestinations}
      onBack={() => setStep(CreateAttributeRuleStep.Details)}
      onSubmit={onSubmit}
      isSubmitting={isLoading}
      errorMessage={error?.message}
    />
  );
};

export const CreateAttributeRuleModal = ({
  open,
  closeModal,
  itemId,
}: ICreateAttributeRuleModalProps): ReactElement => (
  <ModalWrapper closeModal={closeModal} open={open} image={chefPlating} imageAlt="chef plating">
    <CreateAttributeRuleFlow closeModal={closeModal} itemId={itemId} />
  </ModalWrapper>
);

export default CreateAttributeRuleModal;
